/**
 * Certification Periods Service
 *
 * Static service class for all CertificationPeriod API calls.
 * Follows the Component → Service → apiClient pattern — no direct fetch() calls.
 *
 * All methods throw on API failure; callers are responsible for error handling.
 */
import { apiClient } from '../api-client';
import { ApiResponse } from '../../dtos/common/api.dto';
import type {
  CertificationPeriod,
  CreateCertificationPeriodRequest,
  TransitionPhaseRequest,
  UpdateChecklistItemRequest,
  PatientDashboardData
} from '../../dtos/patientmr/certificationPeriod.dto';

export class CertificationPeriodsService {
  /**
   * List all certification periods for a patient, sorted newest first.
   */
  static async list(patientId: string): Promise<CertificationPeriod[]> {
    const response: ApiResponse<CertificationPeriod[]> = await apiClient.get(
      `/patientmr/${patientId}/certification-periods`
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to list certification periods');
  }

  /**
   * Get a single certification period by ID.
   * Note: this endpoint may auto-transition the period to RECERT_DUE on read
   * if the period is ACTIVE and within 14 days of expiration.
   */
  static async get(patientId: string, certificationPeriodId: string): Promise<CertificationPeriod> {
    const response: ApiResponse<CertificationPeriod> = await apiClient.get(
      `/patientmr/${patientId}/certification-periods/${certificationPeriodId}`
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to get certification period');
  }

  /**
   * Create a new certification period in SOC_IN_PROGRESS phase.
   * certificationNumber is auto-computed by the backend.
   * endDate defaults to startDate + 60 days when omitted.
   */
  static async create(
    patientId: string,
    payload: CreateCertificationPeriodRequest
  ): Promise<CertificationPeriod> {
    const response: ApiResponse<CertificationPeriod> = await apiClient.post(
      `/patientmr/${patientId}/certification-periods`,
      payload
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to create certification period');
  }

  /**
   * Update allowed scalar fields on a certification period.
   * Phase cannot be updated via this method — use transition() instead.
   * Allowed fields: pocCertificationId, priorAuthorizationId, primaryPayerId,
   *                 primaryPayerType, primaryPayerName, physicianOrderId
   */
  static async update(
    patientId: string,
    certificationPeriodId: string,
    updates: Partial<Pick<CertificationPeriod,
      | 'pocCertificationId'
      | 'priorAuthorizationId'
      | 'primaryPayerId'
      | 'primaryPayerType'
      | 'primaryPayerName'
    >>
  ): Promise<CertificationPeriod> {
    const response: ApiResponse<CertificationPeriod> = await apiClient.put(
      `/patientmr/${patientId}/certification-periods/${certificationPeriodId}`,
      updates
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to update certification period');
  }

  /**
   * Transition a certification period to a new phase.
   *
   * Returns 409 with { data: { incompleteItems: string[] } } when transitioning to ACTIVE
   * with an incomplete SOC checklist. Callers should handle this specifically to show
   * a modal listing the incomplete items.
   *
   * dischargeReason is required when toPhase === 'DISCHARGE_IN_PROGRESS'.
   */
  static async transition(
    patientId: string,
    certificationPeriodId: string,
    payload: TransitionPhaseRequest
  ): Promise<CertificationPeriod> {
    const response: ApiResponse<CertificationPeriod> = await apiClient.post(
      `/patientmr/${patientId}/certification-periods/${certificationPeriodId}/transition`,
      payload
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to transition certification phase');
  }

  /**
   * Manually confirm a case-close checklist item.
   * Only 'chartReviewComplete' and 'transferDocumentationSent' are confirmable.
   * Period must be in DISCHARGE_IN_PROGRESS or DISCHARGED phase.
   */
  static async updateChecklistItem(
    patientId: string,
    certificationPeriodId: string,
    payload: UpdateChecklistItemRequest
  ): Promise<CertificationPeriod> {
    const response: ApiResponse<CertificationPeriod> = await apiClient.patch(
      `/patientmr/${patientId}/certification-periods/${certificationPeriodId}/checklist`,
      payload
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to update checklist item');
  }

  /**
   * Get aggregated patient dashboard data.
   * Returns current cert period with cached state snapshot, PA summary,
   * care plan summaries, days until expiration, and alert banners.
   *
   * Fast path: 3–5 DynamoDB reads (cached patientState + stateLastComputedAt).
   * Fallback: live recompute if snapshot is stale (>5 min) — up to 11 reads.
   * Backend timeout is 8s.
   */
  static async getDashboard(patientId: string): Promise<PatientDashboardData> {
    const response: ApiResponse<PatientDashboardData> = await apiClient.get(
      `/patientmr/${patientId}/dashboard`
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error?.message || 'Failed to retrieve dashboard data');
  }
}
